import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';

const cores = {
  navy: '#1A2233', border: '#d4dde8', muted: '#6b7a8d', orange: '#D85A30',
  green: '#27ae60', red: '#e74c3c',
};
const inputStyle = { width: '100%', padding: '9px 12px', border: `1.5px solid ${cores.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: cores.muted, display: 'block', marginBottom: 4 };
const btnPrimary = { padding: '10px 20px', background: cores.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const btnGhost = { padding: '10px 20px', background: '#f0f3f7', color: cores.muted, border: `1px solid ${cores.border}`, borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' };

// Cabeçalhos exatos do modelo baixável (também usados como referência de leitura)
const COLUNAS_MODELO = [
  'Mês de conclusão', 'Nome da loja', 'ID da loja', 'Comissão total do vendedor',
  'CNPJ do vendedor', 'CPF do vendedor', 'Identificação Fiscal Estrangeira',
  'Razão social do vendedor', 'Endereço do vendedor', 'País do vendedor',
  'Inscrição Estadual', 'E-mail',
];

const LINHA_EXEMPLO = {
  'Mês de conclusão': 'Julho/2026',
  'Nome da loja': 'Loja Centro',
  'ID da loja': '12',
  'Comissão total do vendedor': 350.5,
  'CNPJ do vendedor': '',
  'CPF do vendedor': '123.456.789-00',
  'Identificação Fiscal Estrangeira': '',
  'Razão social do vendedor': 'João da Silva',
  'Endereço do vendedor': 'Rua Exemplo, 100',
  'País do vendedor': 'BR',
  'Inscrição Estadual': '',
  'E-mail': 'joao@exemplo.com',
};

const MESES = [
  { v: '01', l: 'Janeiro' }, { v: '02', l: 'Fevereiro' }, { v: '03', l: 'Março' },
  { v: '04', l: 'Abril' },   { v: '05', l: 'Maio' },      { v: '06', l: 'Junho' },
  { v: '07', l: 'Julho' },   { v: '08', l: 'Agosto' },    { v: '09', l: 'Setembro' },
  { v: '10', l: 'Outubro' }, { v: '11', l: 'Novembro' },  { v: '12', l: 'Dezembro' },
];
// Ano atual ± 1 — cobre virada de ano sem precisar editar o código todo ano
const ANO_ATUAL = new Date().getFullYear();
const ANOS = [ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1];

function normalizar(s) {
  return (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

// Converte valores monetários em qualquer formato comum de planilha/relatório BR
// para number: "R$3,55" | "R$ 1.234,56" | "1234,56" | "1234.56" | 350.5 (número) → 3.55 / 1234.56 / ...
// Retorna NaN quando não há como interpretar o valor.
function parseValorBR(valorRaw) {
  if (valorRaw === null || valorRaw === undefined || valorRaw === '') return NaN;
  if (typeof valorRaw === 'number') return valorRaw;

  const limpo = String(valorRaw)
    .replace(/[^\d,.-]/g, '')          // remove "R$", espaços e outras letras
    .replace(/\.(?=\d{3}(,|$))/g, '')  // remove ponto de milhar (ex: 1.234,56 → 1234,56)
    .replace(',', '.');                // vírgula decimal → ponto decimal

  const n = parseFloat(limpo);
  return isNaN(n) ? NaN : n;
}

// Mapeia uma linha da planilha (chaves = cabeçalhos originais) para o formato
// que o backend espera em /notas/importar-massa
function mapearLinha(linhaRaw) {
  const chaves = Object.keys(linhaRaw);
  const acha = (...termos) => {
    const k = chaves.find(c => termos.some(t => normalizar(c).includes(normalizar(t))));
    return k ? linhaRaw[k] : '';
  };
  return {
    mes: acha('mes de conclusao', 'mes'),
    nome_loja: acha('nome da loja'),
    valor: parseValorBR(acha('comissao total', 'comissao')), // já normalizado em number aqui
    cnpj: acha('cnpj'),
    cpf: acha('cpf'),
    nome_vendedor: acha('razao social'),
    endereco: acha('endereco'),
    email: acha('e-mail', 'email'),
    // Tomador estrangeiro (fornecedor fora do Brasil) — vem direto do relatório
    // de comissões quando o vendedor não tem CPF/CNPJ nacional
    nif: acha('identificacao fiscal estrangeira', 'nif'),
    pais: (acha('pais do vendedor', 'pais') || 'BR').toString().trim().toUpperCase(),
  };
}

// Reproduz no frontend a mesma regra de montagem de descrição do backend
// (POST /notas/importar-massa), só para exibir a prévia — quem decide de
// verdade é o backend na hora de emitir.
function montarDescricaoPreview(linha, descricaoPadrao, periodoReferencia) {
  const prefixo = (descricaoPadrao || '').trim() || 'Comissão';
  const periodoLabel = periodoReferencia || linha.mes || '';
  return `${prefixo}${periodoLabel ? ' — ' + periodoLabel : ''}${linha.nome_loja ? ' (' + linha.nome_loja + ')' : ''}`;
}

// Cada linha faz uma chamada real à Focus NFe (com pausa de 600ms entre elas no
// backend, pra não estourar o rate limit deles). Um lote de 200 linhas já leva
// ~2-3min só de pausa; múltiplos lotes sequenciais evitam estourar o tempo
// máximo de uma requisição HTTP (Render + navegador).
const TAMANHO_LOTE = 200;

function validarLinha(linha) {
  const estrangeiro = !!linha.pais && linha.pais !== 'BR';
  if (!linha.nome_vendedor) return 'Nome/razão social do vendedor ausente';
  if (estrangeiro) {
    if (!linha.nif) return 'NIF (Identificação Fiscal Estrangeira) ausente';
  } else {
    const doc = (linha.cnpj || '').toString().replace(/\D/g, '') || (linha.cpf || '').toString().replace(/\D/g, '');
    if (!doc) return 'CPF e CNPJ ausentes';
  }
  if (linha.valor === undefined || linha.valor === null || isNaN(linha.valor) || linha.valor <= 0) return 'Valor inválido';
  return null;
}

export default function ImportarNotasModal({ onClose, toast, onSucesso }) {
  const [passo, setPasso] = useState(1); // 1 config/upload | 2 preview | 3 resultado
  const [codigoServico, setCodigoServico] = useState('');
  const [descricaoPadrao, setDescricaoPadrao] = useState('');
  const [periodoMes, setPeriodoMes] = useState('');
  const [periodoAno, setPeriodoAno] = useState('');
  const periodoReferencia = periodoMes && periodoAno ? `${periodoMes}/${periodoAno}` : '';
  const [arquivoNome, setArquivoNome] = useState('');
  const [linhas, setLinhas] = useState([]); // todas as linhas mapeadas, com _erro preenchido quando inválida
  const [processando, setProcessando] = useState(false);
  const [progressoLote, setProgressoLote] = useState(null); // { atual, total, linhasFeitas, linhasTotal }
  const [resultado, setResultado] = useState(null); // { importacao_ids, total, total_sucesso, total_erro, parcial }
  const [detalhe, setDetalhe] = useState(null); // itens combinados de todos os lotes (para o relatório)

  useEffect(() => {
    api.get('/notas/lc116-padrao')
      .then(({ data }) => { if (data.codigo_servico) setCodigoServico(data.codigo_servico); })
      .catch(() => {});
  }, []);

  const baixarModelo = () => {
    const ws = XLSX.utils.json_to_sheet([LINHA_EXEMPLO], { header: COLUNAS_MODELO });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comissões');
    XLSX.writeFile(wb, 'modelo-notas-comissao.xlsx');
  };

  const lerArquivo = async (file) => {
    setArquivoNome(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const primeira = wb.SheetNames[0];
      const linhasRaw = XLSX.utils.sheet_to_json(wb.Sheets[primeira], { defval: '' });
      if (linhasRaw.length === 0) {
        toast('A planilha está vazia.', 'error');
        return;
      }
      const mapeadas = linhasRaw.map(l => {
        const m = mapearLinha(l);
        return { ...m, _erro: validarLinha(m) };
      });
      setLinhas(mapeadas);
      setPasso(2);
    } catch (e) {
      toast('Não foi possível ler o arquivo. Confira se é um .xlsx ou .csv válido.', 'error');
    }
  };

  const validas = linhas.filter(l => !l._erro);
  const invalidas = linhas.filter(l => l._erro);

  // Junta os detalhes (linha a linha) de vários lotes/importações num único
  // relatório, pra exibir no passo 3 como se fosse uma importação só.
  const buscarEJuntarDetalhes = async (importacaoIds) => {
    const detalhes = await Promise.all(
      importacaoIds.map(id => api.get(`/notas/importacoes/${id}`).then(r => r.data).catch(() => null))
    );
    const itens = detalhes.filter(Boolean).flatMap(d => d.itens || []);
    setDetalhe({ itens });
  };

  // Emite uma lista de linhas (já dividida em lotes de TAMANHO_LOTE), uma
  // requisição por lote, em sequência. Se um lote falhar (erro de rede/servidor),
  // os lotes já emitidos com sucesso NÃO são perdidos — ficam disponíveis pra
  // retomar de onde parou.
  const emitirEmLotes = async (linhasParaEmitir) => {
    const lotes = [];
    for (let i = 0; i < linhasParaEmitir.length; i += TAMANHO_LOTE) {
      lotes.push(linhasParaEmitir.slice(i, i + TAMANHO_LOTE));
    }

    let totalSucesso = 0, totalErro = 0;
    const importacaoIds = [];

    for (let i = 0; i < lotes.length; i++) {
      setProgressoLote({
        atual: i + 1, total: lotes.length,
        linhasFeitas: i * TAMANHO_LOTE, linhasTotal: linhasParaEmitir.length,
      });
      try {
        const { data } = await api.post('/notas/importar-massa', {
          linhas: lotes[i], codigo_servico: codigoServico, arquivo_nome: arquivoNome,
          descricao_padrao: descricaoPadrao || undefined,
          periodo_referencia: periodoReferencia || undefined,
        });
        totalSucesso += data.total_sucesso;
        totalErro += data.total_erro;
        importacaoIds.push(data.importacao_id);
      } catch (e) {
        // Lote atual falhou — para por aqui, mas preserva o que já foi emitido
        // e guarda as linhas restantes (deste lote em diante) pra permitir retomar.
        const linhasRestantes = lotes.slice(i).flat();
        setResultado({
          total_sucesso: totalSucesso, total_erro: totalErro,
          total: linhasParaEmitir.length, importacao_ids: importacaoIds,
          parcial: true, linhasRestantes,
        });
        if (importacaoIds.length > 0) await buscarEJuntarDetalhes(importacaoIds);
        toast(`Lote ${i + 1} de ${lotes.length} falhou: ${e?.response?.data?.erro || 'erro de conexão'}. O que já foi emitido está salvo — use "Tentar novamente" para continuar.`, 'error');
        setPasso(3);
        return;
      }
    }

    setResultado({
      total_sucesso: totalSucesso, total_erro: totalErro,
      total: linhasParaEmitir.length, importacao_ids: importacaoIds, parcial: false,
    });
    await buscarEJuntarDetalhes(importacaoIds);
    setPasso(3);
    onSucesso(); // atualiza a listagem em segundo plano, sem fechar o modal
  };

  const confirmarEmitir = async () => {
    if (!codigoServico) { toast('Informe o código do serviço (LC116).', 'error'); return; }
    if (validas.length === 0) { toast('Nenhuma linha válida para emitir.', 'error'); return; }
    setProcessando(true);
    await emitirEmLotes(validas);
    setProgressoLote(null);
    setProcessando(false);
  };

  const tentarNovamenteRestantes = async () => {
    if (!resultado?.linhasRestantes?.length) return;
    setProcessando(true);
    // Mantém os totais já emitidos com sucesso somando ao resultado desta nova rodada
    const acumuladoSucesso = resultado.total_sucesso;
    const acumuladoErro = resultado.total_erro;
    const acumuladoIds = resultado.importacao_ids;
    const linhasRestantes = resultado.linhasRestantes;

    const lotes = [];
    for (let i = 0; i < linhasRestantes.length; i += TAMANHO_LOTE) lotes.push(linhasRestantes.slice(i, i + TAMANHO_LOTE));

    let totalSucesso = acumuladoSucesso, totalErro = acumuladoErro;
    const importacaoIds = [...acumuladoIds];

    for (let i = 0; i < lotes.length; i++) {
      setProgressoLote({ atual: i + 1, total: lotes.length, linhasFeitas: i * TAMANHO_LOTE, linhasTotal: linhasRestantes.length });
      try {
        const { data } = await api.post('/notas/importar-massa', {
          linhas: lotes[i], codigo_servico: codigoServico, arquivo_nome: arquivoNome,
          descricao_padrao: descricaoPadrao || undefined,
          periodo_referencia: periodoReferencia || undefined,
        });
        totalSucesso += data.total_sucesso;
        totalErro += data.total_erro;
        importacaoIds.push(data.importacao_id);
      } catch (e) {
        const restantes = lotes.slice(i).flat();
        setResultado({ total_sucesso: totalSucesso, total_erro: totalErro, total: resultado.total, importacao_ids: importacaoIds, parcial: true, linhasRestantes: restantes });
        await buscarEJuntarDetalhes(importacaoIds);
        toast(`Lote falhou novamente: ${e?.response?.data?.erro || 'erro de conexão'}.`, 'error');
        setProgressoLote(null);
        setProcessando(false);
        return;
      }
    }

    setResultado({ total_sucesso: totalSucesso, total_erro: totalErro, total: resultado.total, importacao_ids: importacaoIds, parcial: false });
    await buscarEJuntarDetalhes(importacaoIds);
    setProgressoLote(null);
    setProcessando(false);
    onSucesso();
  };

  return (
    <div onClick={e => e.target === e.currentTarget && !processando && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>

        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${cores.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: cores.navy }}>📥 Emitir NFS-e em Massa</div>
          {!processando && <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: cores.muted }}>✕</button>}
        </div>

        <div style={{ padding: 24, overflowY: 'auto' }}>

          {/* PASSO 1 — Configuração e upload */}
          {passo === 1 && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>CÓDIGO DO SERVIÇO (LC116) *</label>
                <input value={codigoServico} onChange={e => setCodigoServico(e.target.value)} style={inputStyle} placeholder="Ex: 1706" />
                <div style={{ fontSize: 12, color: cores.muted, marginTop: 4 }}>Usado em todas as notas deste lote. Fica salvo como padrão para a próxima importação.</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>DESCRIÇÃO PADRÃO</label>
                  <input value={descricaoPadrao} onChange={e => setDescricaoPadrao(e.target.value)} style={inputStyle} placeholder="Comissão" />
                </div>
                <div>
                  <label style={labelStyle}>PERÍODO — MÊS (opcional)</label>
                  <select value={periodoMes} onChange={e => setPeriodoMes(e.target.value)} style={inputStyle}>
                    <option value="">—</option>
                    {MESES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>PERÍODO — ANO</label>
                  <select value={periodoAno} onChange={e => setPeriodoAno(e.target.value)} style={inputStyle}>
                    <option value="">—</option>
                    {ANOS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ fontSize: 12, color: cores.muted, marginTop: -8, marginBottom: 20 }}>
                A nota sai com a descrição: <strong>"{montarDescricaoPreview({ nome_loja: 'Nome da Loja' }, descricaoPadrao, periodoReferencia)}"</strong> — se a Descrição Padrão ficar em branco, usa "Comissão".
                Se deixar mês/ano em branco, usa a coluna "Mês de conclusão" de cada linha da planilha.
              </div>

              <button onClick={baixarModelo} style={{ ...btnGhost, marginBottom: 20 }}>⬇️ Baixar modelo de planilha</button>

              <div style={{ marginBottom: 8 }}>
                <label style={labelStyle}>ARQUIVO (.xlsx ou .csv)</label>
                <input type="file" accept=".xlsx,.xls,.csv" onChange={e => e.target.files[0] && lerArquivo(e.target.files[0])} style={inputStyle} />
              </div>
              <div style={{ fontSize: 12, color: cores.muted }}>
                Colunas esperadas: {COLUNAS_MODELO.join(', ')}.
              </div>
            </div>
          )}

          {/* PASSO 2 — Pré-visualização */}
          {passo === 2 && (
            <div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ padding: '8px 14px', background: '#d1fae5', color: '#065f46', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
                  ✅ {validas.length} prontas para emitir
                </div>
                {invalidas.length > 0 && (
                  <div style={{ padding: '8px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
                    ❌ {invalidas.length} com erro (serão ignoradas)
                  </div>
                )}
                {linhas.some(l => l.pais && l.pais !== 'BR') && (
                  <div style={{ padding: '8px 14px', background: '#e0f2fe', color: '#075985', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
                    🌎 {linhas.filter(l => l.pais && l.pais !== 'BR').length} tomador(es) estrangeiro(s)
                  </div>
                )}
              </div>
              {validas.length > TAMANHO_LOTE && (
                <div style={{ fontSize: 12, color: cores.muted, marginTop: -8, marginBottom: 14 }}>
                  Lote grande — será enviado em {Math.ceil(validas.length / TAMANHO_LOTE)} partes de até {TAMANHO_LOTE} linhas, uma de cada vez. Não feche esta janela durante o envio.
                </div>
              )}

              <div style={{ border: `1px solid ${cores.border}`, borderRadius: 10, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1.8fr 1.2fr', gap: 8, padding: '8px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', position: 'sticky', top: 0 }}>
                  <span>Vendedor</span><span>CPF/CNPJ</span><span>Valor</span><span>Descrição</span><span>Situação</span>
                </div>
                {linhas.map((l, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1.8fr 1.2fr', gap: 8, padding: '8px 12px', borderTop: `1px solid ${cores.border}`, fontSize: 13, background: l._erro ? '#fef2f2' : 'white' }}>
                    <span>{l.nome_vendedor || '—'}</span>
                    <span>{l.pais && l.pais !== 'BR' ? `🌎 ${l.pais} · ${l.nif || '—'}` : (l.cnpj || l.cpf || '—')}</span>
                    <span>{typeof l.valor === 'number' && !isNaN(l.valor) ? l.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</span>
                    <span style={{ color: cores.muted, fontSize: 12 }}>{montarDescricaoPreview(l, descricaoPadrao, periodoReferencia)}</span>
                    <span style={{ color: l._erro ? cores.red : cores.green, fontWeight: 600 }}>{l._erro || 'OK'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PASSO 3 — Resultado */}
          {passo === 3 && resultado && (
            <div>
              {resultado.parcial && (
                <div style={{ padding: '10px 14px', background: '#fef3c7', color: '#92400e', borderRadius: 8, fontSize: 13, marginBottom: 14, fontWeight: 600 }}>
                  ⚠️ A importação parou no meio do caminho (falha de conexão). O que já foi emitido está salvo abaixo — clique em "Tentar novamente" para continuar de onde parou ({resultado.linhasRestantes?.length} linha(s) restante(s)).
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <div style={{ padding: '8px 14px', background: '#d1fae5', color: '#065f46', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
                  ✅ {resultado.total_sucesso} emitida(s)
                </div>
                {resultado.total_erro > 0 && (
                  <div style={{ padding: '8px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>
                    ❌ {resultado.total_erro} falharam
                  </div>
                )}
              </div>

              {detalhe && (
                <div style={{ border: `1px solid ${cores.border}`, borderRadius: 10, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 8, padding: '8px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: cores.muted, textTransform: 'uppercase', position: 'sticky', top: 0 }}>
                    <span>Vendedor</span><span>Status</span><span>Detalhe</span>
                  </div>
                  {detalhe.itens.map(it => (
                    <div key={it.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 8, padding: '8px 12px', borderTop: `1px solid ${cores.border}`, fontSize: 13 }}>
                      <span>{it.nome_vendedor || '—'}</span>
                      <span style={{ color: it.status === 'sucesso' ? cores.green : cores.red, fontWeight: 700 }}>
                        {it.status === 'sucesso' ? '✅ Emitida' : '❌ Erro'}
                      </span>
                      <span style={{ color: cores.muted }}>{it.mensagem_erro || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 12, color: cores.muted, marginTop: 10 }}>
                As notas emitidas com sucesso já aparecem na listagem, marcadas como "Importada".
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: `1px solid ${cores.border}`, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div>
            {passo === 2 && <button onClick={() => setPasso(1)} style={btnGhost} disabled={processando}>← Voltar</button>}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {passo !== 3 && <button onClick={onClose} style={btnGhost} disabled={processando}>Cancelar</button>}
            {passo === 2 && (
              <button onClick={confirmarEmitir} style={btnPrimary} disabled={processando || validas.length === 0}>
                {processando
                  ? (progressoLote
                      ? `Lote ${progressoLote.atual}/${progressoLote.total} — ${Math.min(progressoLote.linhasFeitas + TAMANHO_LOTE, progressoLote.linhasTotal)}/${progressoLote.linhasTotal} linhas...`
                      : `Emitindo ${validas.length} nota(s)...`)
                  : `Confirmar e Emitir (${validas.length})`}
              </button>
            )}
            {passo === 3 && resultado?.parcial && (
              <button onClick={tentarNovamenteRestantes} style={btnPrimary} disabled={processando}>
                {processando && progressoLote
                  ? `Lote ${progressoLote.atual}/${progressoLote.total} — ${Math.min(progressoLote.linhasFeitas + TAMANHO_LOTE, progressoLote.linhasTotal)}/${progressoLote.linhasTotal} linhas...`
                  : `🔄 Tentar novamente (${resultado.linhasRestantes?.length} restante(s))`}
              </button>
            )}
            {passo === 3 && <button onClick={onClose} style={btnPrimary} disabled={processando}>Concluir</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
